import { Controller } from "@hotwired/stimulus"
import { patch, post } from "@rails/request.js"
import { fieldToken, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"

// "License Details" modal for the State Licenses card. Opened via the
// `state-license-details:open` document event (dispatched by the
// state-license-picker with the tile's current values). One-step save:
//
// * add mode (no updateUrl — the license row doesn't exist yet): number,
//   expiration (or never-expires), and PDF are all required and POST together
//   to the transactional upsert endpoint; the returned Turbo Stream swaps the
//   tile straight to its uploaded state.
// * edit mode (updateUrl present — persisted license): pre-filled from the
//   tile. Choosing a replacement PDF POSTs to the same upsert endpoint (new
//   document + details in one request); leaving it empty PATCHes details only.
//
// Both endpoints answer failures as 422 plain text, shown inline so the modal
// stays open with the user's input intact.
//
// Error lifecycle: an inline error stays until the next open() or save(),
// with ONE exception — the "attach the PDF" validation is the only error a
// file choice resolves, so choosing a file (the shared file-drop slot's
// change event) clears exactly that one. Number/date validation and server
// 422 messages are never cleared by a file choice, and nothing reads as
// uploaded until the save succeeds.
//
// Events (not Stimulus outlets) because the modal lives OUTSIDE the card while
// tiles get Turbo-Stream-swapped inside it — matching the licensed-states-modal
// pattern on the same screen.
//
// BUTTON-9: Save License is disabled (with its reason) until a field differs
// from what open() loaded — the number, date, never-expires box or a chosen
// PDF — and again if the edit is reverted. In add mode the loaded fields are
// blank, so that means "something has been entered".
const MISSING_PDF_MESSAGE = "Attach the license PDF."
const MISSING_PDF_CODE = "missing_pdf"

export default class extends Controller {
  static targets = ["stateLabel", "numberInput", "dateInput", "neverInput", "message", "saveButton",
                    "fileInput", "fileLabel", "currentFile", "currentFileName"]

  static values = { uploadUrl: String }

  connect() {
    this.boundOpen = this.open.bind(this)
    this.boundSync = this._syncSaveButton.bind(this)
    document.addEventListener("state-license-details:open", this.boundOpen)
    this.element.addEventListener("input", this.boundSync)
    this.element.addEventListener("change", this.boundSync)
    this._takeBaseline()
    this._syncSaveButton()
  }

  disconnect() {
    document.removeEventListener("state-license-details:open", this.boundOpen)
    this.element.removeEventListener("input", this.boundSync)
    this.element.removeEventListener("change", this.boundSync)
  }

  open(event) {
    const { code, name, licenseNumber, expiresOn, neverExpires, updateUrl, pdfFilename } = event.detail
    this.openContext = { code, updateUrl: updateUrl || null }
    this.stateLabelTarget.textContent =
      `${name} (${code}) — enter this state license's details.`
    this.numberInputTarget.value = licenseNumber || ""
    this.neverInputTarget.checked = neverExpires === true
    this.dateInputTarget.value = neverExpires ? "" : (expiresOn || "")
    this._resetFileInput()
    this._renderFileField(pdfFilename)
    this.toggleNever()
    this._hideError()
    this._takeBaseline()
    this._syncSaveButton()

    const Modal = window.bootstrap?.Modal
    if (Modal) Modal.getOrCreateInstance(this.element).show()
  }

  // change->state-license-details#toggleNever
  toggleNever() {
    const never = this.neverInputTarget.checked
    this.dateInputTarget.disabled = never
    if (never) this.dateInputTarget.value = ""
  }

  // click->state-license-details#save
  async save() {
    const details = this._collect()
    if (!details) return

    const file = this.fileInputTarget.files[0] || null
    if (!file && !this.openContext?.updateUrl) {
      this._showError(MISSING_PDF_MESSAGE, { code: MISSING_PDF_CODE })
      return
    }

    if (file) {
      await this._submit(() =>
        post(this.uploadUrlValue, { body: this._uploadBody(details, file), responseKind: "turbo-stream" }))
    } else {
      await this._submit(() =>
        patch(this.openContext.updateUrl, { body: this._detailsBody(details), responseKind: "turbo-stream" }))
    }
  }

  // change->state-license-details#fileChanged — appended to the slot's input
  // through its extra_input_actions local. A chosen file resolves the
  // missing-PDF validation and nothing else: any other inline error (number,
  // date, a server 422) keeps showing until the next save or open. Removing
  // the chosen file again shows nothing new — the user has not tried to save.
  fileChanged() {
    if (this.fileInputTarget.files.length === 0) return
    if (this.shownErrorCode === MISSING_PDF_CODE) this._hideError()
  }

  // The picker is the shared file-drop slot: clearing the input alone would
  // leave its selected-file chips on screen, so the reset is announced as a
  // bubbling change — the same signal the slot's own remove button sends.
  _resetFileInput() {
    this.fileInputTarget.value = ""
    this.fileInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Add mode: required-PDF field, no current-file chip. Edit mode: optional
  // replace, with the current-document chip always visible (named when the
  // tile knew the filename) so leaving the picker empty never reads as
  // destructive. A persisted row with NO document — details imported from a
  // saved NIPR report (#14709) — says so: the chip must never imply a file
  // exists. The input's required flag follows the mode so assistive tech
  // hears the same thing the label says.
  _renderFileField(pdfFilename) {
    const editing = Boolean(this.openContext.updateUrl)
    this.fileInputTarget.required = !editing
    if (editing) {
      this.fileLabelTarget.textContent = "Replace PDF (optional)"
      this.currentFileNameTarget.textContent = pdfFilename || "No document on file"
      this.currentFileTarget.classList.remove("d-none")
    } else {
      this.fileLabelTarget.textContent = "License PDF (required)"
      this.currentFileNameTarget.textContent = ""
      this.currentFileTarget.classList.add("d-none")
    }
  }

  // Validated field values, or null (with an inline error shown) when invalid.
  _collect() {
    const licenseNumber = this.numberInputTarget.value.trim()
    const neverExpires = this.neverInputTarget.checked
    const expiresOn = neverExpires ? "" : this.dateInputTarget.value

    if (!licenseNumber) {
      this._showError("Enter the license number.")
      return null
    }
    if (!neverExpires && !expiresOn) {
      this._showError('Enter the expiration date, or check "This license never expires".')
      return null
    }
    return { licenseNumber, expiresOn, neverExpires }
  }

  // One-step create / PDF replace: the upsert endpoint's licenses[CODE] +
  // license_details[CODE] wire format.
  _uploadBody(details, file) {
    const code = this.openContext.code
    const body = new FormData()
    body.append(`licenses[${code}]`, file)
    body.append(`license_details[${code}][license_number]`, details.licenseNumber)
    body.append(`license_details[${code}][expires_on]`, details.expiresOn)
    body.append(`license_details[${code}][never_expires]`, details.neverExpires ? "1" : "0")
    return body
  }

  _detailsBody(details) {
    const body = new FormData()
    body.append("agent_state_license[license_number]", details.licenseNumber)
    body.append("agent_state_license[expires_on]", details.expiresOn)
    body.append("agent_state_license[never_expires]", details.neverExpires ? "1" : "0")
    return body
  }

  // The fields' state, compared with what open() loaded. A file input's value
  // is read-only, so the chosen PDF is fingerprinted by name.
  _fingerprint() {
    const file = this.fileInputTarget.files?.[0]
    return [this.numberInputTarget, this.dateInputTarget, this.neverInputTarget]
      .map(fieldToken)
      .concat(`file:${file ? file.name : ""}`)
      .join("|")
  }

  _takeBaseline() {
    this.baseline = this._fingerprint()
  }

  _syncSaveButton() {
    if (this.submitting) return
    const dirty = this._fingerprint() !== this.baseline
    syncGateReason(this.saveButtonTarget, { ready: dirty, reason: SAVE_UNCHANGED_REASON })
  }

  // Shared submit path: request.js handles CSRF and auto-renders the success
  // Turbo Stream (tile swap + toast). Failures are 422 plain text shown inline.
  async _submit(requestFn) {
    this.submitting = true
    this.saveButtonTarget.disabled = true
    try {
      const response = await requestFn()
      if (response.ok) {
        this._closeModal()
      } else {
        const message = ((await response.text) || "").trim()
        this._showError(message || "Could not save license details. Please try again.")
      }
    } catch {
      this._showError("Could not save license details. Please try again.")
    } finally {
      this.submitting = false
      this._syncSaveButton()
    }
  }

  _closeModal() {
    const Modal = window.bootstrap?.Modal
    if (!Modal) return
    Modal.getOrCreateInstance(this.element).hide()
  }

  // `code` names the one validation a later user action may resolve on its
  // own (see fileChanged); errors shown without a code stay until save/open.
  _showError(message, { code = null } = {}) {
    this.shownErrorCode = code
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = message
    this.messageTarget.classList.remove("d-none")
  }

  _hideError() {
    this.shownErrorCode = null
    if (this.hasMessageTarget) this.messageTarget.classList.add("d-none")
  }
}
